import React, { useRef, useEffect } from 'react'

import { useConfiguratorContext } from '@augmatica/iframe-interface-react'

import {
  setIsSaveProjectToMailModalOpen,
  setIsSaveProjectOfferModalOpen,
  setIsSummaryPanelVisible,
  setSelectedToolbarTabIndex,
  setSelectedToolbarDoorSubTab,
  setSelectedToolbarGarageSubTab,
  setViewInside,
} from '../../store/ui/uiSlice'

import { FULFILLED } from '../../constants/constants'
import { getHtmlMessageForDivergentProperties } from '../../helpers/getHtmlMessageForDivergentProperties'

import Heading from '../Heading/Heading'
import AddToCart from '../AppHeader/ProceedButton/AddToCart'
import AlertBox from '../AlertBox/AlertBox'
import PriceSummary from '../PriceSummary/PriceSummary'
import PartPaymentWidget from '../PartPaymentWidget/PartPaymentWidget'
import DeliveryInfo from '../DeliveryInfo/DeliveryInfo'
import DelayedDeliveryToggle from '../DelayedDeliveryToggle/DelayedDeliveryToggle'
import InternalArticlesTable from '../_internal/CompleteArticlesTable/CompleteArticlesTable'

import * as Styled from './styles'
import UserConfiguration from '../UserConfiguration/UserConfiguration'
import { useAppDispatch, useAppSelector } from '../../hooks'

interface Props {
  onRequestSaveProject: (args: { type: string }) => {
    internalSaveUuidv4: string
  }
  onDelayedDeliveryChange: (isSelected: boolean) => void
}

const SummaryPanel: React.FC<Props> = ({ onRequestSaveProject, onDelayedDeliveryChange }) => {
  const dispatch = useAppDispatch()
  const { isStoreLoggedIn, content, languageCode } = useAppSelector((state) => state.init)
  const { lfcItems, renderItems, withDivergentProperties } = useAppSelector((state) => state.items)
  const { isSummaryPanelVisible } = useAppSelector((state) => state.ui)
  const { requestStatus_fetchPrices_aggregated } = useAppSelector((state) => state.requests.requestStatus)
  const {
    meta,
    props: { loftLeft, loftRight, loftFloor },
  } = useAppSelector((state) => state.configResult) as any

  const summaryPanelRef = useRef(null)

  const configurator = useConfiguratorContext()

  useEffect(() => {
    setViewInside(false)

    configurator.setCameraView('front')
    configurator.setVisibility({ roof: true })
  }, [])

  return (
    isSummaryPanelVisible && (
      <Styled.SummaryPanel ref={summaryPanelRef} isEditItemPanelActive={isSummaryPanelVisible} data-testid="summary-panel">
        <Styled.EmphasizedContent>
          <Styled.PanelHeader level={1}>{content?.summary_panel?.design?.title}</Styled.PanelHeader>
          <Styled.Text>{content?.summary_panel?.design?.description}</Styled.Text>
          {meta && (
            <Styled.FactBox>
              <Heading level={3}>{content?.summary_panel?.design?.facts}</Heading>
              <p>
                <b>{content?.summary_panel?.design?.building_area}</b>
                <span>{meta.buildingArea.toString().replace('.', ',')} m²</span>
              </p>
              <p>
                <b>{content?.summary_panel?.design?.height}</b>
                <span>{meta.height.toString().replace('.', ',')} m</span>
              </p>
              <p>
                <b>{content?.summary_panel?.design?.snow_zone}</b>
                <span>{meta.snowZone}</span>
              </p>
              <p>
                <b>{content?.summary_panel?.design?.number_of_doors}</b>
                <span>{meta.doors}</span>
              </p>
              <p>
                <b>{content?.summary_panel?.design?.number_of_windows}</b>
                <span>{meta.windows}</span>
              </p>
              <p>
                <b>{content?.tabs?.[2]?.title}</b>
                {loftLeft.value || loftRight.value ? (
                  <span>
                    <span>
                      {loftLeft.value
                        ? `${content?.props?.loft?.constructionPlan?.positioning?.left} ${loftLeft.value.toString().replace('.', ',')} m²`
                        : ''}
                    </span>
                    <span>
                      {loftRight.value
                        ? `${content?.props?.loft?.constructionPlan?.positioning?.right} ${loftRight.value.toString().replace('.', ',')} m²`
                        : ''}
                    </span>
                    <span>{loftFloor.value === 'chip' ? content?.props?.loft?.floor?.options?.[1]?.title : ''}</span>
                  </span>
                ) : (
                  <span>{content?.props?.gutters?.options?.none?.title}</span>
                )}
              </p>
            </Styled.FactBox>
          )}
          {Object.values(withDivergentProperties).some(
            (divergentProperties) => Object.keys(divergentProperties).length
          ) && (
            <AlertBox
              title={content?.other?.divergentPropertiesTitle}
              htmlMessage={getHtmlMessageForDivergentProperties(
                withDivergentProperties,
                content,
                lfcItems,
                renderItems,
                languageCode
              )}
              callToAction={{
                text: content?.other?.divergentPropertiesCta,
                onClick: () => {
                  const [firstItemGroup] =
                    Object.entries(withDivergentProperties).find(
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      ([_, divergentProperties]) => Object.keys(divergentProperties).length
                    ) ?? []
                  switch (firstItemGroup) {
                    case 'doors':
                      dispatch(setSelectedToolbarDoorSubTab(0))
                      dispatch(setSelectedToolbarTabIndex(3))
                      break
                    case 'doubleDoors':
                      dispatch(setSelectedToolbarDoorSubTab(1))
                      dispatch(setSelectedToolbarTabIndex(3))
                      break
                    case 'windows':
                      dispatch(setSelectedToolbarTabIndex(4))
                      break
                    case 'garageDoors':
                      dispatch(setSelectedToolbarGarageSubTab(0))
                      dispatch(setSelectedToolbarTabIndex(5))
                      break
                    case 'slidingDoors':
                      dispatch(setSelectedToolbarGarageSubTab(1))
                      dispatch(setSelectedToolbarTabIndex(5))
                      break
                    case 'facadeSections':
                      dispatch(setSelectedToolbarGarageSubTab(2))
                      dispatch(setSelectedToolbarTabIndex(5))
                      break
                    default:
                      throw new Error(
                        `Unexpected, not able to find firstItemGroup ${firstItemGroup} in onClick callback for AlertBox (SummaryPanel)`
                      )
                  }
                  dispatch(setIsSummaryPanelVisible(false))
                },
                direction: 'back',
              }}
            />
          )}
          <Heading level={2}>{content?.summary_panel?.save_project?.title}</Heading>
          <Styled.Text>{content?.summary_panel?.save_project?.description}</Styled.Text>
          <Styled.Cta
            disabled={requestStatus_fetchPrices_aggregated !== FULFILLED}
            onClick={() => {
              if (requestStatus_fetchPrices_aggregated === FULFILLED) {
                dispatch(setIsSaveProjectToMailModalOpen(true))
              }
            }}
          >
            <i className="far fa-inbox-in" aria-hidden="true" />
            {content?.summary_panel?.save_project?.cta}

            <i className="fas fa-chevron-right" aria-hidden="true"></i>
          </Styled.Cta>
          <Heading level={2}>{content?.summary_panel?.blueprints?.title}</Heading>
          <Styled.Text>{content?.summary_panel?.blueprints?.description}</Styled.Text>
          <Styled.Cta
            disabled={requestStatus_fetchPrices_aggregated !== FULFILLED}
            onClick={() => {
              if (requestStatus_fetchPrices_aggregated === FULFILLED) {
                dispatch(setIsSaveProjectOfferModalOpen(true))
              }
            }}
          >
            <i className="far fa-inbox-in" aria-hidden="true" />
            {content?.summary_panel?.blueprints?.cta}

            <i className="fas fa-chevron-right" aria-hidden="true"></i>
          </Styled.Cta>
          <PriceSummary insideSummaryPanel />
          <Heading level={2}>{content?.summary_panel?.delivery_time}</Heading>
          <Styled.DeliveryGroup>
            <DeliveryInfo />
            <DelayedDeliveryToggle className="delayed-delivery-toggle" onChange={onDelayedDeliveryChange} />
          </Styled.DeliveryGroup>
          <AddToCart
            onRequestSaveProject={onRequestSaveProject}
            insideSummaryPanel={isSummaryPanelVisible}
            testId="summary"
          />
          <PartPaymentWidget insideSummaryPanel />
        </Styled.EmphasizedContent>

        <Styled.AdditionalContent>
          <Heading level={2}>{content?.summary_panel?.configuration?.title}</Heading>
          <UserConfiguration />

          <Heading level={2}>{content?.summary_panel?.always_included?.title}</Heading>
          {content?.summary_panel?.always_included?.items?.map((item: any, index: number) => (
            <Styled.IncludeItem key={item + index}>
              <i className="fas fa-check"></i>
              <div>
                <Heading level={3}>{item.title}</Heading>
                <Styled.Text>{item.description}</Styled.Text>
              </div>
            </Styled.IncludeItem>
          ))}
        </Styled.AdditionalContent>

        <Styled.EmphasizedContent>
          <Heading level={2}>{content?.summary_panel?.contact?.title}</Heading>
          <Styled.Text>{content?.summary_panel?.contact?.description}</Styled.Text>
          <Styled.Text>{content?.summary_panel?.contact?.telephone}</Styled.Text>
          <Styled.Email href={`mailto:${content?.summary_panel?.contact?.email}`}>
            {content?.summary_panel?.contact?.email}
          </Styled.Email>
          {content?.summary_panel?.contact?.digitalMeeting?.href &&
            content?.summary_panel?.contact?.digitalMeeting?.text && (
              <Styled.Link target="_blank" href={content?.summary_panel?.contact?.digitalMeeting?.href}>
                {content?.summary_panel?.contact?.digitalMeeting?.text}
              </Styled.Link>
            )}
        </Styled.EmphasizedContent>

        {isStoreLoggedIn && (
          <Styled.AdditionalContent>
            <Heading level={3}>Complete list of articles</Heading>
            <InternalArticlesTable />
          </Styled.AdditionalContent>
        )}
      </Styled.SummaryPanel>
    )
  )
}

export default SummaryPanel
